import { ReactiveFormHostDiagnostics } from './reactive-form-host-diagnostics';

export interface ReactiveFormHostDestroyableRequest {
    readonly unsuscribe?: (() => void) | undefined;
    readonly unsubscribe?: (() => void) | undefined;
}

export interface ReactiveFormHostDestroyError {
    readonly index: number;
    readonly message: string;
}

export interface ReactiveFormHostDestroyResult {
    readonly destroyed: number;
    readonly failed: number;
    readonly errors: readonly ReactiveFormHostDestroyError[];
}

export interface ReactiveFormHostDestroyOptions {
    readonly host?: object | null | undefined;
    readonly sources?: readonly unknown[] | null | undefined;
    readonly diagnostics?: ReactiveFormHostDiagnostics | undefined;
}

const destroyStartedCode = 'host.destroy.started';
const destroyCompletedCode = 'host.destroy.completed';
const destroyFailedCode = 'host.destroy.failed';

/** Centralizes host destruction helpers without importing application request classes. */
export class ReactiveFormHostDestroyController {
    public static destroy(options: ReactiveFormHostDestroyOptions = {}): ReactiveFormHostDestroyResult {
        const destroyables = this.collectDestroyables(options);

        options.diagnostics?.info({
            code: destroyStartedCode,
            message: 'Reactive form host destruction started.',
            context: { destroyables: destroyables.length },
        });

        const errors: ReactiveFormHostDestroyError[] = [];
        let destroyed = 0;

        destroyables.forEach((destroyable, index) => {
            try {
                this.destroyOne(destroyable);
                destroyed += 1;
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Unknown destroy error';

                errors.push({ index, message });
                options.diagnostics?.error({
                    code: destroyFailedCode,
                    message: 'Reactive form host destroyable failed.',
                    context: { index, message },
                });
            }
        });

        options.diagnostics?.info({
            code: destroyCompletedCode,
            message: 'Reactive form host destruction completed.',
            context: { destroyed, failed: errors.length },
        });

        return {
            destroyed,
            failed: errors.length,
            errors,
        };
    }

    public static collectDestroyables(options: ReactiveFormHostDestroyOptions = {}): readonly ReactiveFormHostDestroyableRequest[] {
        const explicitSources = options.sources ?? [];
        const hostSources = options.host ? Object.values(options.host as Record<string, unknown>) : [];
        const seen = new Set<ReactiveFormHostDestroyableRequest>();
        const destroyables: ReactiveFormHostDestroyableRequest[] = [];

        for (const source of [...explicitSources, ...hostSources]) {
            if (!this.isDestroyableRequest(source) || seen.has(source)) {
                continue;
            }

            seen.add(source);
            destroyables.push(source);
        }

        return destroyables;
    }

    private static destroyOne(destroyable: ReactiveFormHostDestroyableRequest): void {
        const destroy = typeof destroyable.unsuscribe === 'function'
            ? destroyable.unsuscribe
            : destroyable.unsubscribe;

        destroy?.call(destroyable);
    }

    private static isDestroyableRequest(source: unknown): source is ReactiveFormHostDestroyableRequest {
        if (typeof source !== 'object' || source === null) {
            return false;
        }

        const candidate = source as ReactiveFormHostDestroyableRequest;

        return typeof candidate.unsuscribe === 'function' || typeof candidate.unsubscribe === 'function';
    }
}
